import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Mail, Save, Eye, EyeOff, Copy, Check, Send, X, Code, Type,
         Bold, Italic, Underline, Link, AlignLeft, AlignCenter, AlignRight,
         List, ListOrdered, Users, Clock, Trash2 } from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';

const SAMPLE_MERGE_VALUES = {
  business_name:      'Acme Staffing',
  first_name:         'Sarah',
  dashboard_url:      'https://surfox.ai',
  year:               String(new Date().getFullYear()),
  messages_sent:      '47',
  responses_received: '12',
  hot_leads_count:    '3',
  response_rate:      '25.5%',
};

const MERGE_TAGS = [
  { tag: '{{business_name}}',      description: 'Tenant business name' },
  { tag: '{{dashboard_url}}',      description: 'SurFox dashboard URL' },
  { tag: '{{messages_sent}}',      description: 'Messages sent (day-10 only)' },
  { tag: '{{responses_received}}', description: 'Responses received (day-10 only)' },
  { tag: '{{hot_leads_count}}',    description: 'Hot leads surfaced (day-10 only)' },
  { tag: '{{response_rate}}',      description: 'Response rate % (day-10 only)' },
  { tag: '{{year}}',               description: 'Current calendar year' },
];

const BROADCAST_MERGE_TAGS = [
  { tag: '{{business_name}}', description: 'Tenant business name' },
  { tag: '{{first_name}}',    description: 'Recipient first name' },
  { tag: '{{year}}',          description: 'Current calendar year' },
];

const TEMPLATE_ORDER = [
  'tenant_welcome',
  'no_campaign_day2',
  'campaign_activated_day2',
  'campaign_activated_day5',
  'trial_midpoint_day10',
  'trial_ending_day13',
];

// ── Native rich text editor (no external deps, React 19 safe) ────────────────
function RichTextEditor({ value, onChange, editorKey }) {
  const ref = useRef(null);
  const [linkModal, setLinkModal] = useState({ open: false, label: '', url: '' });
  const savedSelectionRef = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = value || '';
    }
  }, [editorKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
  };

  const isUrl = (str) => /^https?:\/\//i.test(str.trim()) || /^www\./i.test(str.trim());

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    sel.removeAllRanges();
    if (savedSelectionRef.current) {
      sel.addRange(savedSelectionRef.current);
    }
  };

  const insertLink = () => {
    const sel = window.getSelection();
    const selectedText = sel.toString().trim();
    saveSelection();

    if (isUrl(selectedText)) {
      setLinkModal({ open: true, label: selectedText, url: selectedText });
    } else {
      setLinkModal({ open: true, label: selectedText, url: 'https://' });
    }
  };

  const applyLink = () => {
    const { label, url } = linkModal;
    if (!url || url === 'https://') { setLinkModal({ open: false, label: '', url: '' }); return; }

    ref.current?.focus();
    restoreSelection();

    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const a = document.createElement('a');
      a.href = url;
      a.textContent = label || url;
      a.target = '_blank';
      range.insertNode(a);
      // Move cursor after the link
      range.setStartAfter(a);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    onChange(ref.current.innerHTML);
    setLinkModal({ open: false, label: '', url: '' });
    savedSelectionRef.current = null;
  };

  const cancelLink = () => {
    setLinkModal({ open: false, label: '', url: '' });
    savedSelectionRef.current = null;
    ref.current?.focus();
  };

  const ToolBtn = ({ onClick, title, children }) => (
    <button
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      className="p-1.5 rounded text-gray-600 hover:bg-gray-200 transition-colors"
    >
      {children}
    </button>
  );

  const Sep = () => <div className="w-px h-5 bg-gray-300 mx-1" />;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden relative">
      <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
        <ToolBtn onClick={() => exec('bold')} title="Bold"><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="Italic"><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('underline')} title="Underline"><Underline className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec('justifyLeft')} title="Align left"><AlignLeft className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyCenter')} title="Align center"><AlignCenter className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyRight')} title="Align right"><AlignRight className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Bullet list"><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={insertLink} title="Insert link"><Link className="w-4 h-4" /></ToolBtn>
        <Sep />
        <select
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { exec('fontSize', e.target.value); e.target.value = ''; }}
          className="text-xs border border-gray-200 rounded px-1 py-1 bg-white text-gray-600"
          defaultValue=""
        >
          <option value="" disabled>Size</option>
          {[1,2,3,4,5,6,7].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select
          onMouseDown={e => e.stopPropagation()}
          onChange={e => { exec('foreColor', e.target.value); e.target.value = ''; }}
          className="text-xs border border-gray-200 rounded px-1 py-1 bg-white text-gray-600"
          defaultValue=""
        >
          <option value="" disabled>Color</option>
          {['#000000','#334155','#6366f1','#8b5cf6','#10b981','#ef4444','#f59e0b','#ffffff'].map(c => (
            <option key={c} value={c} style={{ background: c, color: c === '#ffffff' ? '#000' : '#fff' }}>{c}</option>
          ))}
        </select>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current.innerHTML)}
        className="p-4 focus:outline-none text-sm text-gray-800 overflow-y-auto"
        style={{ minHeight: '400px', maxHeight: '600px' }}
      />

      {/* Link insertion modal */}
      {linkModal.open && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-5 w-96 border border-gray-200"
               onMouseDown={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Insert Link</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                <input
                  type="text"
                  value={linkModal.label}
                  onChange={e => setLinkModal(m => ({ ...m, label: e.target.value }))}
                  placeholder="Link text (leave blank to show URL)"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
                <input
                  type="text"
                  value={linkModal.url}
                  onChange={e => setLinkModal(m => ({ ...m, url: e.target.value }))}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  onKeyDown={e => { if (e.key === 'Enter') applyLink(); if (e.key === 'Escape') cancelLink(); }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={cancelLink}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
                Cancel
              </button>
              <button onClick={applyLink}
                      className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function replaceMergeTags(text, values) {
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => values[key] ?? match);
}

function MergeTagPanel({ tags, copiedTag, onCopy }) {
  return (
    <div className="w-56 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Merge Tags</h3>
      <p className="text-xs text-gray-400 mb-4">Click to copy</p>
      <div className="space-y-3">
        {tags.map(({ tag, description }) => (
          <div key={tag}>
            <button onClick={() => onCopy(tag)} className="w-full text-left group" title="Click to copy">
              <code className={`flex items-center justify-between text-xs px-2 py-1.5 rounded font-mono transition-colors ${
                copiedTag === tag
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-indigo-600 group-hover:bg-indigo-50'
              }`}>
                <span>{tag}</span>
                {copiedTag === tag
                  ? <Check className="w-3 h-3 flex-shrink-0" />
                  : <Copy className="w-3 h-3 flex-shrink-0 opacity-40 group-hover:opacity-100" />
                }
              </code>
            </button>
            <p className="text-xs text-gray-400 mt-0.5 px-1">{description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EmailManager() {
  // ── Template editor state ────────────────────────────────────────────────
  const [templates, setTemplates]             = useState([]);
  const [selected, setSelected]               = useState(null);
  const [form, setForm]                       = useState({ subject: '', html_body: '', is_active: false });
  const [editorMode, setEditorMode]           = useState('visual');
  const [loading, setLoading]                 = useState(true);
  const [saving, setSaving]                   = useState(false);
  const [saveSuccess, setSaveSuccess]         = useState(false);
  const [saveError, setSaveError]             = useState(null);
  const [showPreview, setShowPreview]         = useState(false);
  const [copiedTag, setCopiedTag]             = useState(null);
  const [testEmail, setTestEmail]             = useState('');
  const [testSending, setTestSending]         = useState(false);
  const [testResult, setTestResult]           = useState(null);
  const [showTestPopover, setShowTestPopover] = useState(false);
  const testInputRef = useRef(null);

  // ── Tenant picker (shared between template preview and broadcast) ─────────
  const [tenants, setTenants]                     = useState([]);
  const [selectedTenantId, setSelectedTenantId]   = useState('');
  const [tenantMergeValues, setTenantMergeValues] = useState(null);
  const [tenantLoading, setTenantLoading]         = useState(false);

  // ── Broadcast state ───────────────────────────────────────────────────────
  const [broadcastMode, setBroadcastMode]               = useState(false);
  const [broadcastForm, setBroadcastForm]               = useState({ subject: '', html_body: '', header_title: '', header_subtitle: '', from_name: 'SurFox', from_email: 'welcome@surfox.ai', reply_to: 'admin@surfox.ai' });
  const [broadcastEditorMode, setBroadcastEditorMode]   = useState('visual');
  const [broadcastRecipientMode, setBroadcastRecipientMode] = useState('all'); // 'all' | 'specific' | 'manual'
  const [broadcastSelectedIds, setBroadcastSelectedIds] = useState([]);
  const [broadcastManualTo, setBroadcastManualTo]       = useState('');
  const [broadcastManualCc, setBroadcastManualCc]       = useState('');
  const [broadcastManualBcc, setBroadcastManualBcc]     = useState('');
  const [broadcastSending, setBroadcastSending]         = useState(false);
  const [broadcastResult, setBroadcastResult]           = useState(null);
  const [broadcastShowPreview, setBroadcastShowPreview] = useState(false);
  const [broadcastScheduleMode, setBroadcastScheduleMode] = useState('now'); // 'now' | 'scheduled'
  const [broadcastScheduleDate, setBroadcastScheduleDate] = useState('');
  const [broadcastScheduledList, setBroadcastScheduledList] = useState([]);
  const [broadcastConfirming, setBroadcastConfirming]   = useState(false);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchTenants = useCallback(async () => {
    const { data } = await supabase.from('tenants').select('id, name').or(VISIBLE_TENANTS_OR).order('name');
    if (data) setTenants(data);
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('email_templates')
      .select('id, name, email_type, subject, html_body, is_active, updated_at');
    if (!error && data) {
      const sorted = [...data].sort((a, b) => {
        const ai = TEMPLATE_ORDER.indexOf(a.email_type);
        const bi = TEMPLATE_ORDER.indexOf(b.email_type);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      setTemplates(sorted);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTemplates(); fetchTenants(); }, [fetchTemplates, fetchTenants]);

  // ── Tenant picker logic ───────────────────────────────────────────────────
  const handleTenantChange = async (tenantId) => {
    setSelectedTenantId(tenantId);
    if (!tenantId) { setTenantMergeValues(null); return; }
    setTenantLoading(true);
    try {
      const tenant = tenants.find(t => t.id === tenantId);
      const [{ data: metrics }, { data: adminUsers }] = await Promise.all([
        supabase.rpc('get_control_room_metrics', {
          p_tenant_id:   tenantId,
          p_campaign_id: null,
          p_date_range:  'allTime',
        }),
        supabase
          .from('users_profile')
          .select('first_name, full_name')
          .eq('tenant_id', tenantId)
          .neq('role', 'user')
          .limit(1),
      ]);
      const adminUser = adminUsers?.[0];
      const firstName =
        adminUser?.first_name ||
        (adminUser?.full_name ? adminUser.full_name.split(' ')[0] : '') ||
        tenant?.name ||
        'there';
      setTenantMergeValues({
        business_name:      tenant?.name || 'there',
        first_name:         firstName,
        dashboard_url:      'https://surfox.ai',
        year:               String(new Date().getFullYear()),
        messages_sent:      String(metrics?.messageVolume ?? 0),
        responses_received: String(metrics?.responseRate  ?? 0),
        hot_leads_count:    String(metrics?.totalHotLeads ?? 0),
        response_rate:      `${metrics?.responseRate      ?? 0}%`,
      });
    } finally {
      setTenantLoading(false);
    }
  };

  const activeMergeValues = tenantMergeValues ?? SAMPLE_MERGE_VALUES;

  // ── Template editor actions ───────────────────────────────────────────────
  const selectTemplate = (tpl) => {
    setBroadcastMode(false);
    setSelected(tpl);
    setForm({ subject: tpl.subject || '', html_body: tpl.html_body || '', is_active: tpl.is_active });
    setShowPreview(false);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true); setSaveSuccess(false); setSaveError(null);
    const { error } = await supabase
      .from('email_templates')
      .update({ subject: form.subject, html_body: form.html_body, is_active: form.is_active, updated_at: new Date().toISOString() })
      .eq('id', selected.id);
    setSaving(false);
    if (error) {
      setSaveError(error.message);
    } else {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setSelected(prev => prev ? { ...prev, ...form } : prev);
      await fetchTemplates();
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !selected) return;
    setTestSending(true); setTestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: {
          recipient_email: testEmail,
          subject:         replaceMergeTags(form.subject,   activeMergeValues),
          html_body:       replaceMergeTags(form.html_body, activeMergeValues),
        },
      });
      if (error) setTestResult({ ok: false, message: error.message || 'Send failed' });
      else       setTestResult({ ok: true,  message: data?.message || `Sent to ${testEmail}` });
    } catch (err) {
      setTestResult({ ok: false, message: err.message });
    } finally {
      setTestSending(false);
    }
  };

  const openTestPopover = () => {
    setTestResult(null);
    setShowTestPopover(true);
    setTimeout(() => testInputRef.current?.focus(), 50);
  };

  // ── Broadcast actions ─────────────────────────────────────────────────────
  const openBroadcast = () => {
    setSelected(null);
    setBroadcastMode(true);
    setBroadcastResult(null);
    setBroadcastConfirming(false);
    fetchScheduledBroadcasts();
  };

  const toggleBroadcastTenant = (id) => {
    setBroadcastSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const parseEmails = (str) => str.split(/[,;]+/).map(e => {
    const match = e.match(/<([^>]+)>/);
    return (match ? match[1] : e).trim();
  }).filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

  const broadcastRecipientCount = broadcastRecipientMode === 'all'
    ? tenants.length
    : broadcastRecipientMode === 'specific'
      ? broadcastSelectedIds.length
      : parseEmails(broadcastManualTo).length;

  const broadcastHasRecipients = broadcastRecipientMode === 'all'
    || (broadcastRecipientMode === 'specific' && broadcastSelectedIds.length > 0)
    || (broadcastRecipientMode === 'manual' && parseEmails(broadcastManualTo).length > 0);

  const buildBroadcastPayload = () => {
    const payload = {
      subject:          broadcastForm.subject,
      html_body:        broadcastForm.html_body,
      header_title:     broadcastForm.header_title  || null,
      header_subtitle:  broadcastForm.header_subtitle || null,
      from_name:        broadcastForm.from_name     || 'SurFox',
      from_email:       broadcastForm.from_email    || 'welcome@surfox.ai',
      reply_to:         broadcastForm.reply_to      || null,
      recipient_mode:   broadcastRecipientMode,
    };
    if (broadcastRecipientMode === 'manual') {
      payload.manual_to  = parseEmails(broadcastManualTo);
      payload.manual_cc  = parseEmails(broadcastManualCc);
      payload.manual_bcc = parseEmails(broadcastManualBcc);
    } else {
      payload.tenant_ids = broadcastRecipientMode === 'all' ? 'all' : broadcastSelectedIds;
    }
    return payload;
  };

  const handleSendBroadcast = async () => {
    if (!broadcastForm.subject || !broadcastForm.html_body || !broadcastHasRecipients) return;
    setBroadcastSending(true); setBroadcastResult(null); setBroadcastConfirming(false);
    try {
      const { data, error } = await supabase.functions.invoke('send-broadcast-email', { body: buildBroadcastPayload() });
      if (error) setBroadcastResult({ ok: false, message: error.message });
      else       setBroadcastResult({ ok: true,  message: data?.message, sent: data?.sent, failed: data?.failed });
    } catch (err) {
      setBroadcastResult({ ok: false, message: err.message });
    } finally {
      setBroadcastSending(false);
    }
  };

  const handleScheduleBroadcast = async () => {
    if (!broadcastForm.subject || !broadcastForm.html_body || !broadcastHasRecipients || !broadcastScheduleDate) return;
    setBroadcastSending(true); setBroadcastResult(null); setBroadcastConfirming(false);
    try {
      const payload = buildBroadcastPayload();
      const row = {
        scheduled_at:     new Date(broadcastScheduleDate).toISOString(),
        subject:          payload.subject,
        html_body:        payload.html_body,
        header_title:     payload.header_title,
        header_subtitle:  payload.header_subtitle,
        from_name:        payload.from_name,
        from_email:       payload.from_email,
        reply_to:         payload.reply_to,
        recipient_mode:   payload.recipient_mode,
        tenant_ids:       payload.tenant_ids ? (payload.tenant_ids === 'all' ? ['all'] : payload.tenant_ids) : null,
        manual_to:        payload.manual_to  || null,
        manual_cc:        payload.manual_cc  || null,
        manual_bcc:       payload.manual_bcc || null,
      };
      const { error } = await supabase.from('scheduled_broadcasts').insert(row);
      if (error) {
        setBroadcastResult({ ok: false, message: error.message });
      } else {
        setBroadcastResult({ ok: true, message: `Broadcast scheduled for ${new Date(broadcastScheduleDate).toLocaleString()}` });
        fetchScheduledBroadcasts();
      }
    } catch (err) {
      setBroadcastResult({ ok: false, message: err.message });
    } finally {
      setBroadcastSending(false);
    }
  };

  const fetchScheduledBroadcasts = useCallback(async () => {
    const { data } = await supabase
      .from('scheduled_broadcasts')
      .select('id, subject, scheduled_at, status, recipient_mode, result')
      .in('status', ['pending', 'processing'])
      .order('scheduled_at', { ascending: true });
    setBroadcastScheduledList(data || []);
  }, []);

  const cancelScheduledBroadcast = async (id) => {
    await supabase.from('scheduled_broadcasts').update({ status: 'cancelled' }).eq('id', id);
    fetchScheduledBroadcasts();
  };

  // ── Shared helpers ────────────────────────────────────────────────────────
  const copyTag = (tag) => {
    navigator.clipboard.writeText(tag).then(() => {
      setCopiedTag(tag);
      setTimeout(() => setCopiedTag(null), 1500);
    });
  };

  // ── Derived preview values ────────────────────────────────────────────────
  const broadcastBase = templates.find(t => t.email_type === 'broadcast_base');

  const previewSubject = showPreview ? replaceMergeTags(form.subject,   activeMergeValues) : '';
  const previewHtml    = showPreview ? replaceMergeTags(form.html_body, activeMergeValues) : '';
  const broadcastPreviewValues = { ...activeMergeValues };
  const headerTitleHtml = broadcastForm.header_title
    ? `<h1 style="color:#ffffff;font-size:22px;font-weight:700;margin:16px 0 4px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;">${broadcastForm.header_title}</h1>`
    : '';
  const headerSubtitleHtml = broadcastForm.header_subtitle
    ? `<p style="color:#94a3b8;font-size:14px;margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;">${broadcastForm.header_subtitle}</p>`
    : '';
  const broadcastWrapped = broadcastBase
    ? broadcastBase.html_body
        .replace('{{header_title}}', headerTitleHtml)
        .replace('{{header_subtitle}}', headerSubtitleHtml)
        .replace('{{content}}', broadcastForm.html_body)
    : broadcastForm.html_body;
  const broadcastPreviewHtml    = broadcastShowPreview ? replaceMergeTags(broadcastWrapped,            broadcastPreviewValues) : '';
  const broadcastPreviewSubject = broadcastShowPreview ? replaceMergeTags(broadcastForm.subject,       broadcastPreviewValues) : '';

  // Templates shown in the list (broadcast_base is internal only)
  const listedTemplates = templates.filter(t => t.email_type !== 'broadcast_base');

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full min-h-screen bg-gray-50">

      {/* ── Sidebar ───────────────────────────────────────────── */}
      <div className="w-72 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">

        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-500" />
            <h1 className="text-base font-semibold text-gray-900">Email Manager</h1>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {listedTemplates.length} template{listedTemplates.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => { setBroadcastMode(false); }}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              !broadcastMode
                ? 'text-indigo-600 border-b-2 border-indigo-500 bg-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            Templates
          </button>
          <button
            onClick={openBroadcast}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              broadcastMode
                ? 'text-indigo-600 border-b-2 border-indigo-500 bg-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            Broadcast
          </button>
        </div>

        {/* Tenant picker */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Preview as tenant</label>
          <select
            value={selectedTenantId}
            onChange={e => handleTenantChange(e.target.value)}
            className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">— Sample data —</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {tenantLoading && <p className="text-xs text-gray-400 mt-1">Loading metrics…</p>}
          {tenantMergeValues && !tenantLoading && <p className="text-xs text-green-600 mt-1">✓ Using live data</p>}
        </div>

        {/* Template list */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {loading ? (
            <div className="p-4 text-sm text-gray-400">Loading…</div>
          ) : (
            listedTemplates.map(tpl => (
              <button
                key={tpl.id}
                onClick={() => selectTemplate(tpl)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                  selected?.id === tpl.id && !broadcastMode
                    ? 'bg-indigo-50 border-l-2 border-indigo-500'
                    : 'border-l-2 border-transparent'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-gray-900 truncate">{tpl.name}</span>
                  <span className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                    tpl.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {tpl.is_active ? 'Active' : 'Off'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{tpl.email_type}</p>
                {tpl.updated_at && (
                  <p className="text-xs text-gray-300 mt-0.5">{new Date(tpl.updated_at).toLocaleDateString()}</p>
                )}
              </button>
            ))
          )}
        </div>

      </div>

      {/* ── Main content ──────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* ── BROADCAST MODE ─────────────────────────────────── */}
        {broadcastMode ? (
          <>
            {/* Broadcast header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Broadcast Email</h2>
                <p className="text-xs text-gray-400">Sends immediately to all selected tenant accounts</p>
              </div>
              <div className="flex items-center gap-3">

                {/* Visual / HTML toggle */}
                {!broadcastShowPreview && (
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setBroadcastEditorMode('visual')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        broadcastEditorMode === 'visual'
                          ? 'bg-white text-gray-900 shadow-sm font-medium'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Type className="w-3.5 h-3.5" />Visual
                    </button>
                    <button
                      onClick={() => setBroadcastEditorMode('html')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        broadcastEditorMode === 'html'
                          ? 'bg-white text-gray-900 shadow-sm font-medium'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Code className="w-3.5 h-3.5" />HTML
                    </button>
                  </div>
                )}

                <button
                  onClick={() => setBroadcastShowPreview(p => !p)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {broadcastShowPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {broadcastShowPreview ? 'Edit' : 'Preview'}
                </button>

                {/* Send / Schedule / Confirm button */}
                {broadcastConfirming ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                      {broadcastScheduleMode === 'scheduled'
                        ? `Schedule for ${new Date(broadcastScheduleDate).toLocaleString()}?`
                        : `Send to ${broadcastRecipientCount} ${broadcastRecipientMode === 'manual' ? 'recipient' : 'tenant'}${broadcastRecipientCount !== 1 ? 's' : ''}?`}
                    </span>
                    <button
                      onClick={broadcastScheduleMode === 'scheduled' ? handleScheduleBroadcast : handleSendBroadcast}
                      disabled={broadcastSending}
                      className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {broadcastScheduleMode === 'scheduled' ? <Clock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                      {broadcastSending ? 'Processing…' : broadcastScheduleMode === 'scheduled' ? 'Confirm Schedule' : 'Confirm Send'}
                    </button>
                    <button
                      onClick={() => setBroadcastConfirming(false)}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setBroadcastConfirming(true)}
                    disabled={!broadcastForm.subject || !broadcastForm.html_body || broadcastSending || !broadcastHasRecipients || (broadcastScheduleMode === 'scheduled' && !broadcastScheduleDate)}
                    className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors ${
                      broadcastScheduleMode === 'scheduled'
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {broadcastScheduleMode === 'scheduled' ? <Clock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    {broadcastScheduleMode === 'scheduled' ? 'Schedule Broadcast' : 'Send Broadcast'}
                  </button>
                )}
              </div>
            </div>

            {/* Broadcast result banner */}
            {broadcastResult && (
              <div className={`mx-6 mt-4 px-4 py-3 rounded-lg text-sm border flex items-center justify-between ${
                broadcastResult.ok
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <span>{broadcastResult.ok ? '✓ ' : '✗ '}{broadcastResult.message}</span>
                <button onClick={() => setBroadcastResult(null)} className="opacity-50 hover:opacity-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Broadcast preview */}
            {broadcastShowPreview ? (
              <div className="flex-1 overflow-auto p-6">
                <div className="max-w-2xl mx-auto">
                  <div className="bg-gray-100 rounded-lg px-4 py-2.5 mb-4 text-sm text-gray-700">
                    <span className="font-medium text-gray-500 mr-2">Subject:</span>
                    {broadcastPreviewSubject || <em className="text-gray-400">No subject</em>}
                  </div>
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <iframe
                      srcDoc={broadcastPreviewHtml}
                      className="w-full"
                      style={{ height: '640px', border: 'none' }}
                      sandbox="allow-same-origin"
                      title="Broadcast Preview"
                    />
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-3">
                    {tenantMergeValues
                      ? `Rendered with live data from "${tenants.find(t => t.id === selectedTenantId)?.name}" — wrapped in broadcast_base template`
                      : 'Rendered with sample values — wrapped in broadcast_base template'}
                  </p>
                </div>
              </div>

            ) : (
              /* Broadcast edit */
              <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">

                  {/* Recipients */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Recipients</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {broadcastRecipientMode === 'manual'
                            ? 'Send to specific email addresses'
                            : <>Sends to <code className="text-xs bg-gray-100 px-1 rounded">users_profile</code> where role ≠ user</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 font-medium">
                          {broadcastRecipientCount} {broadcastRecipientMode === 'manual' ? 'recipient' : 'tenant'}{broadcastRecipientCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {/* All / Specific / Manual toggle */}
                    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 mb-3 w-fit">
                      {['all', 'specific', 'manual'].map(mode => (
                        <button
                          key={mode}
                          onClick={() => setBroadcastRecipientMode(mode)}
                          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                            broadcastRecipientMode === mode
                              ? 'bg-white text-gray-900 shadow-sm font-medium'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {mode === 'all' ? 'All Tenants' : mode === 'specific' ? 'Specific Tenants' : 'Manual'}
                        </button>
                      ))}
                    </div>

                    {/* Tenant checklist */}
                    {broadcastRecipientMode === 'specific' && (
                      <>
                        <div className="flex items-center gap-3 mb-2">
                          <button
                            onClick={() => setBroadcastSelectedIds(tenants.map(t => t.id))}
                            className="text-xs text-indigo-600 hover:underline"
                          >
                            Select all
                          </button>
                          <button
                            onClick={() => setBroadcastSelectedIds([])}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            Clear
                          </button>
                        </div>
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                          {tenants.map(t => (
                            <label
                              key={t.id}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={broadcastSelectedIds.includes(t.id)}
                                onChange={() => toggleBroadcastTenant(t.id)}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-sm text-gray-700">{t.name}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Manual To / CC / BCC */}
                    {broadcastRecipientMode === 'manual' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">To <span className="text-red-400">*</span></label>
                          <input
                            type="text"
                            value={broadcastManualTo}
                            onChange={e => setBroadcastManualTo(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="email@example.com, another@example.com"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">CC <span className="text-xs text-gray-400 font-normal">(optional)</span></label>
                          <input
                            type="text"
                            value={broadcastManualCc}
                            onChange={e => setBroadcastManualCc(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="cc@example.com"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">BCC <span className="text-xs text-gray-400 font-normal">(optional)</span></label>
                          <input
                            type="text"
                            value={broadcastManualBcc}
                            onChange={e => setBroadcastManualBcc(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="bcc@example.com"
                          />
                        </div>
                        <p className="text-xs text-gray-400">Separate multiple addresses with commas. Merge tags won't be resolved for manual recipients.</p>
                      </div>
                    )}
                  </div>

                  {/* From / Reply-To */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-sm font-medium text-gray-900 mb-3">Sender</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">From Name</label>
                        <input
                          type="text"
                          value={broadcastForm.from_name}
                          onChange={e => setBroadcastForm(f => ({ ...f, from_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="SurFox"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">From Email</label>
                        <input
                          type="text"
                          value={broadcastForm.from_email}
                          onChange={e => setBroadcastForm(f => ({ ...f, from_email: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="welcome@surfox.ai"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Reply-To <span className="text-xs text-gray-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        value={broadcastForm.reply_to}
                        onChange={e => setBroadcastForm(f => ({ ...f, reply_to: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="admin@surfox.ai"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">From email must be a verified domain in Resend.</p>
                  </div>

                  {/* Delivery: Send Now / Schedule */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <p className="text-sm font-medium text-gray-900 mb-3">Delivery</p>
                    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 mb-3 w-fit">
                      <button
                        onClick={() => setBroadcastScheduleMode('now')}
                        className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                          broadcastScheduleMode === 'now'
                            ? 'bg-white text-gray-900 shadow-sm font-medium'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Send Now
                      </button>
                      <button
                        onClick={() => setBroadcastScheduleMode('scheduled')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                          broadcastScheduleMode === 'scheduled'
                            ? 'bg-white text-gray-900 shadow-sm font-medium'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Clock className="w-3.5 h-3.5" />Schedule
                      </button>
                    </div>
                    {broadcastScheduleMode === 'scheduled' && (
                      <div>
                        <input
                          type="datetime-local"
                          value={broadcastScheduleDate}
                          onChange={e => setBroadcastScheduleDate(e.target.value)}
                          min={new Date().toISOString().slice(0, 16)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        {broadcastScheduleDate && (
                          <p className="text-xs text-gray-500 mt-1.5">
                            Will send at {new Date(broadcastScheduleDate).toLocaleString()} (your local time)
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Scheduled broadcasts queue */}
                  {broadcastScheduledList.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm font-medium text-gray-900 mb-3">Scheduled Broadcasts</p>
                      <div className="divide-y divide-gray-100">
                        {broadcastScheduledList.map(b => (
                          <div key={b.id} className="flex items-center justify-between py-2">
                            <div>
                              <p className="text-sm text-gray-800">{b.subject}</p>
                              <p className="text-xs text-gray-400">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {new Date(b.scheduled_at).toLocaleString()} · {b.recipient_mode} · {b.status}
                              </p>
                            </div>
                            {b.status === 'pending' && (
                              <button
                                onClick={() => cancelScheduledBroadcast(b.id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                                title="Cancel"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subject */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line</label>
                    <input
                      type="text"
                      value={broadcastForm.subject}
                      onChange={e => setBroadcastForm(f => ({ ...f, subject: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter subject line… (supports {{business_name}}, {{first_name}})"
                    />
                  </div>

                  {/* Header text (appears under the fox logo) */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Header Text</label>
                    <p className="text-xs text-gray-400 mb-3">
                      Appears under the fox logo in the dark header. Both fields are optional and support merge tags.
                    </p>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={broadcastForm.header_title}
                        onChange={e => setBroadcastForm(f => ({ ...f, header_title: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g. 5 Hot Leads Ready for You, {{business_name}}"
                      />
                      <input
                        type="text"
                        value={broadcastForm.header_subtitle}
                        onChange={e => setBroadcastForm(f => ({ ...f, header_subtitle: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g. Your Monday 3/30 Action Plan"
                      />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Body</label>
                    <p className="text-xs text-gray-400 mb-3">
                      Write the body content only — the SurFox header and footer are added automatically.
                    </p>
                    {broadcastEditorMode === 'visual' ? (
                      <RichTextEditor
                        editorKey="broadcast"
                        value={broadcastForm.html_body}
                        onChange={val => setBroadcastForm(f => ({ ...f, html_body: val }))}
                      />
                    ) : (
                      <textarea
                        value={broadcastForm.html_body}
                        onChange={e => setBroadcastForm(f => ({ ...f, html_body: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                        rows={22}
                        placeholder="Enter HTML body content…"
                      />
                    )}
                  </div>
                </div>

                {/* Merge tag panel */}
                <MergeTagPanel tags={BROADCAST_MERGE_TAGS} copiedTag={copiedTag} onCopy={copyTag} />
              </div>
            )}
          </>

        ) : !selected ? (
          /* ── Empty state ────────────────────────────────────── */
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Select a template to edit</p>
              <p className="text-xs mt-1 opacity-70">or use Broadcast Email to send a one-off</p>
            </div>
          </div>

        ) : (
          /* ── TEMPLATE EDITOR MODE ───────────────────────────── */
          <>
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{selected.name}</h2>
                <p className="text-xs text-gray-400">{selected.email_type}</p>
              </div>
              <div className="flex items-center gap-3">
                {!showPreview && (
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setEditorMode('visual')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        editorMode === 'visual'
                          ? 'bg-white text-gray-900 shadow-sm font-medium'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Type className="w-3.5 h-3.5" />Visual
                    </button>
                    <button
                      onClick={() => setEditorMode('html')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        editorMode === 'html'
                          ? 'bg-white text-gray-900 shadow-sm font-medium'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Code className="w-3.5 h-3.5" />HTML
                    </button>
                  </div>
                )}

                <button
                  onClick={() => setShowPreview(p => !p)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPreview ? 'Edit' : 'Preview'}
                </button>

                {/* Send Test popover */}
                <div className="relative">
                  <button
                    onClick={openTestPopover}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Send className="w-4 h-4" />Send Test
                  </button>
                  {showTestPopover && (
                    <div className="absolute right-0 top-10 z-50 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-gray-900">Send Test Email</p>
                        <button onClick={() => { setShowTestPopover(false); setTestResult(null); }} className="text-gray-400 hover:text-gray-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        {tenantMergeValues
                          ? `Renders with live data from "${tenants.find(t => t.id === selectedTenantId)?.name}".`
                          : 'Renders with sample values. Select a tenant for live data.'}
                      </p>
                      <input
                        ref={testInputRef}
                        type="email"
                        value={testEmail}
                        onChange={e => setTestEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSendTest()}
                        placeholder="you@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3"
                      />
                      {testResult && (
                        <p className={`text-xs mb-3 ${testResult.ok ? 'text-green-600' : 'text-red-600'}`}>
                          {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
                        </p>
                      )}
                      <button
                        onClick={handleSendTest}
                        disabled={testSending || !testEmail}
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        <Send className="w-4 h-4" />
                        {testSending ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : saveSuccess ? '✓ Saved' : 'Save'}
                </button>
              </div>
            </div>

            {saveError && (
              <div className="mx-6 mt-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {saveError}
              </div>
            )}

            {/* Preview mode */}
            {showPreview ? (
              <div className="flex-1 overflow-auto p-6">
                <div className="max-w-2xl mx-auto">
                  <div className="bg-gray-100 rounded-lg px-4 py-2.5 mb-4 text-sm text-gray-700">
                    <span className="font-medium text-gray-500 mr-2">Subject:</span>
                    {previewSubject || <em className="text-gray-400">No subject</em>}
                  </div>
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <iframe
                      srcDoc={previewHtml}
                      className="w-full"
                      style={{ height: '640px', border: 'none' }}
                      sandbox="allow-same-origin"
                      title="Email Preview"
                    />
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-3">
                    {tenantMergeValues
                      ? `Rendered with live data from "${tenants.find(t => t.id === selectedTenantId)?.name}"`
                      : 'Rendered with sample merge tag values'}
                  </p>
                </div>
              </div>

            ) : (
              /* Edit mode */
              <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">

                  {/* Active toggle */}
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Active</p>
                      <p className="text-xs text-gray-500 mt-0.5">When enabled, emails of this type will be sent by the worker</p>
                    </div>
                    <button
                      onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        form.is_active ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        form.is_active ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {/* Subject */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line</label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter subject line…"
                    />
                  </div>

                  {/* Body */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Body</label>
                    {editorMode === 'visual' ? (
                      <RichTextEditor
                        editorKey={selected?.id}
                        value={form.html_body}
                        onChange={val => setForm(f => ({ ...f, html_body: val }))}
                      />
                    ) : (
                      <textarea
                        value={form.html_body}
                        onChange={e => setForm(f => ({ ...f, html_body: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                        rows={22}
                        placeholder="Enter HTML email body…"
                      />
                    )}
                  </div>
                </div>

                <MergeTagPanel tags={MERGE_TAGS} copiedTag={copiedTag} onCopy={copyTag} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
